import React, { useState, useEffect } from 'react';
import {
  FaTimes,
  FaCode,
  FaDatabase,
  FaCopy,
  FaSearch,
  FaChevronDown,
  FaChevronRight,
  FaCheck,
  FaInfoCircle,
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import api from '../../../../services/api';

const TemplateVariablesModal = ({ template, onClose }) => {
  const [availableQueries, setAvailableQueries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedQuery, setExpandedQuery] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const [activeSection, setActiveSection] = useState('queries'); // queries | variables | helpers

  useEffect(() => {
    loadQueries();
  }, []);

  const loadQueries = async () => {
    try {
      setLoading(true);
      const response = await api.telegramBI.templates.getAvailableQueries();
      const data = response.data?.data || response.data || [];
      setAvailableQueries(data);
      if (data.length > 0) {
        setExpandedQuery(data[0].id);
      }
    } catch (error) {
      console.error('Error loading available queries:', error);
      toast.error('Failed to load available queries');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = (text, label) => {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        setCopiedId(label);
        toast.success('Copied to clipboard');
        setTimeout(() => setCopiedId(null), 2000);
      })
      .catch(() => {
        toast.error('Failed to copy');
      });
  };

  const filteredQueries = availableQueries.filter(
    (q) =>
      q.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (q.description && q.description.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const commonVariables = [
    { name: 'data', type: 'array', desc: 'Array of query result rows' },
    { name: 'meta.rowCount', type: 'number', desc: 'Number of rows returned' },
    { name: 'meta.generatedAt', type: 'date', desc: 'Template generation timestamp' },
    { name: 'meta.templateName', type: 'string', desc: 'Name of the current template' },
    { name: 'meta.executionTime', type: 'number', desc: 'Query execution time in ms' },
  ];

  const helperReference = [
    {
      category: 'Data',
      items: [
        { name: 'formatNumber', syntax: '{{formatNumber value}}', desc: 'Locale-formatted number' },
        {
          name: 'formatDate',
          syntax: '{{formatDate value "full"}}',
          desc: 'Formatted date output',
        },
        { name: 'truncate', syntax: '{{truncate text 50}}', desc: 'Truncate text to length' },
      ],
    },
    {
      category: 'Logic',
      items: [
        { name: '#if', syntax: '{{#if condition}}...{{/if}}', desc: 'Conditional rendering' },
        { name: '#each', syntax: '{{#each data}}...{{/each}}', desc: 'Iterate over arrays' },
        {
          name: '#unless',
          syntax: '{{#unless condition}}...{{/unless}}',
          desc: 'Negative conditional',
        },
      ],
    },
    {
      category: 'Comparison',
      items: [
        { name: '#ifEquals', syntax: '{{#ifEquals a b}}...{{/ifEquals}}', desc: 'Equality check' },
        {
          name: '#ifGreater',
          syntax: '{{#ifGreater a b}}...{{/ifGreater}}',
          desc: 'Greater than check',
        },
        { name: '#ifLess', syntax: '{{#ifLess a b}}...{{/ifLess}}', desc: 'Less than check' },
      ],
    },
    {
      category: 'Format',
      items: [
        { name: 'bold', syntax: '{{bold text}}', desc: 'Bold text output' },
        { name: 'italic', syntax: '{{italic text}}', desc: 'Italic text output' },
        { name: 'code', syntax: '{{code text}}', desc: 'Monospace text' },
      ],
    },
    {
      category: 'Layout',
      items: [
        { name: 'table', syntax: '{{table data columns}}', desc: 'Formatted data table' },
        { name: 'chart', syntax: '{{chart "bar" data}}', desc: 'Inline chart rendering' },
        {
          name: '#drilldown',
          syntax: '{{#drilldown "section"}}...{{/drilldown}}',
          desc: 'Collapsible section',
        },
      ],
    },
    {
      category: 'Query',
      items: [
        { name: 'query', syntax: '{{query "QueryName"}}', desc: 'Inline query results' },
        {
          name: '#query',
          syntax: '{{#query "QueryName"}}...{{/query}}',
          desc: 'Query result iteration block',
        },
        {
          name: 'queryCount',
          syntax: '{{queryCount "QueryName"}}',
          desc: 'Number of query results',
        },
        {
          name: '#ifQueryHasResults',
          syntax: '{{#ifQueryHasResults "QueryName"}}...{{/ifQueryHasResults}}',
          desc: 'Conditional on query results',
        },
      ],
    },
  ];

  const typeColor = {
    string: 'bg-blue-50 text-blue-700 border-blue-200',
    number: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    date: 'bg-amber-50 text-amber-700 border-amber-200',
    array: 'bg-violet-50 text-violet-700 border-violet-200',
    boolean: 'bg-rose-50 text-rose-700 border-rose-200',
    object: 'bg-gray-50 text-gray-700 border-gray-200',
  };

  const CopyButton = ({ text, label }) => (
    <button
      onClick={() => handleCopy(text, label)}
      className="p-1 rounded text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors flex-shrink-0"
      title="Copy to clipboard"
    >
      {copiedId === label ? (
        <FaCheck className="text-emerald-500 text-[10px]" />
      ) : (
        <FaCopy className="text-[10px]" />
      )}
    </button>
  );

  const sections = [
    { id: 'queries', label: 'Queries', icon: FaDatabase, count: availableQueries.length },
    { id: 'variables', label: 'Variables', icon: FaInfoCircle, count: commonVariables.length },
    {
      id: 'helpers',
      label: 'Helpers',
      icon: FaCode,
      count: helperReference.reduce((a, c) => a + c.items.length, 0),
    },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        className="absolute inset-0 bg-gray-900/60 dark:bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="relative bg-surface rounded-xl shadow-2xl max-w-3xl w-full mx-4 max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-violet-50 flex items-center justify-center">
              <FaCode className="text-violet-600 text-sm" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Template Variables</h2>
              <p className="text-xs text-gray-500">
                Available references for{' '}
                <span className="font-medium text-gray-700">
                  {template?.template_name || 'template'}
                </span>
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-md hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <FaTimes />
          </button>
        </div>

        {/* Section tabs */}
        <div className="flex gap-0 px-6 border-b border-gray-100">
          {sections.map((section) => {
            const Icon = section.icon;
            return (
              <button
                key={section.id}
                onClick={() => setActiveSection(section.id)}
                className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                  activeSection === section.id
                    ? 'border-primary-500 text-primary-700'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <Icon className="text-[10px]" />
                {section.label}
                <span className="text-[10px] bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded-full ml-0.5">
                  {section.count}
                </span>
              </button>
            );
          })}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {/* Queries Section */}
          {activeSection === 'queries' && (
            <div className="p-5">
              {/* Search */}
              <div className="relative mb-4">
                <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
                <input
                  type="text"
                  placeholder="Search queries..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg bg-surface focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 outline-none transition-all"
                />
              </div>

              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="w-6 h-6 border-2 border-gray-300 border-t-primary-500 rounded-full animate-spin" />
                </div>
              ) : filteredQueries.length === 0 ? (
                <div className="text-center py-12">
                  <FaDatabase className="mx-auto text-2xl text-gray-300 mb-2" />
                  <p className="text-sm text-gray-500">
                    {searchTerm ? 'No queries match your search' : 'No queries available'}
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  {filteredQueries.map((query) => (
                    <div
                      key={query.id}
                      className="border border-gray-200 rounded-lg overflow-hidden"
                    >
                      {/* Query header */}
                      <button
                        onClick={() =>
                          setExpandedQuery(expandedQuery === query.id ? null : query.id)
                        }
                        className="flex items-center justify-between w-full px-4 py-3 text-left hover:bg-gray-50/50 transition-colors"
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          {expandedQuery === query.id ? (
                            <FaChevronDown className="text-[9px] text-gray-400 flex-shrink-0" />
                          ) : (
                            <FaChevronRight className="text-[9px] text-gray-400 flex-shrink-0" />
                          )}
                          <span className="text-sm font-medium text-gray-900 truncate">
                            {query.name}
                          </span>
                          <span
                            className={`text-[10px] px-1.5 py-0.5 rounded ${
                              query.isPublic
                                ? 'bg-blue-50 text-blue-600'
                                : 'bg-gray-100 text-gray-500'
                            }`}
                          >
                            {query.isPublic ? 'Public' : 'Private'}
                          </span>
                        </div>
                        <CopyButton
                          text={`{{#query "${query.name}"}}\n  {{#each this}}\n    {{this.[Field Name]}}\n  {{/each}}\n{{/query}}`}
                          label={`query-${query.id}`}
                        />
                      </button>

                      {/* Query details (expanded) */}
                      {expandedQuery === query.id && (
                        <div className="px-4 pb-4 border-t border-gray-100 bg-gray-50/30">
                          {query.description && (
                            <p className="text-xs text-gray-500 mt-3 mb-3">{query.description}</p>
                          )}

                          {/* Reference snippets */}
                          <div className="text-[11px] font-medium text-gray-500 uppercase tracking-wider mb-2 mt-2">
                            Reference Snippets
                          </div>
                          <div className="space-y-1.5">
                            {[
                              {
                                label: 'Inline reference',
                                code: `{{query "${query.name}"}}`,
                                desc: 'Insert query results inline',
                              },
                              {
                                label: 'Block iteration',
                                code: `{{#query "${query.name}"}}\n  {{#each this}}\n    {{this.[Field Name]}}\n  {{/each}}\n{{/query}}`,
                                desc: 'Loop through query result rows',
                              },
                              {
                                label: 'Result count',
                                code: `{{queryCount "${query.name}"}}`,
                                desc: 'Number of result rows',
                              },
                              {
                                label: 'Conditional',
                                code: `{{#ifQueryHasResults "${query.name}"}}\n  Query has results\n{{/ifQueryHasResults}}`,
                                desc: 'Show content only if query returns data',
                              },
                            ].map((snippet) => (
                              <div
                                key={snippet.label}
                                className="flex items-start justify-between gap-2 px-3 py-2 bg-surface rounded-md border border-gray-200"
                              >
                                <div className="flex-1 min-w-0">
                                  <div className="text-xs font-medium text-gray-700">
                                    {snippet.label}
                                  </div>
                                  <code className="text-[11px] font-mono text-violet-600 block mt-0.5 whitespace-pre-wrap break-all">
                                    {snippet.code}
                                  </code>
                                  <div className="text-[10px] text-gray-400 mt-0.5">
                                    {snippet.desc}
                                  </div>
                                </div>
                                <CopyButton
                                  text={snippet.code}
                                  label={`${query.id}-${snippet.label}`}
                                />
                              </div>
                            ))}
                          </div>

                          {/* Available fields */}
                          {query.fields && query.fields.length > 0 && (
                            <div className="mt-3">
                              <div className="text-[11px] font-medium text-gray-500 uppercase tracking-wider mb-2">
                                Result Fields
                              </div>
                              <div className="flex flex-wrap gap-1">
                                {query.fields.map((field, idx) => (
                                  <button
                                    key={idx}
                                    onClick={() =>
                                      handleCopy(`{{this.[${field}]}}`, `field-${query.id}-${idx}`)
                                    }
                                    className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-mono bg-surface border border-gray-200 rounded hover:bg-primary-50 hover:border-primary-200 hover:text-primary-700 transition-colors cursor-pointer"
                                    title={`Copy {{this.[${field}]}}`}
                                  >
                                    {copiedId === `field-${query.id}-${idx}` ? (
                                      <FaCheck className="text-emerald-500 text-[8px]" />
                                    ) : (
                                      <FaCopy className="text-gray-300 text-[8px]" />
                                    )}
                                    {field}
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Variables Section */}
          {activeSection === 'variables' && (
            <div className="p-5">
              <div className="text-[11px] font-medium text-gray-500 uppercase tracking-wider mb-3">
                Common Template Variables
              </div>
              <div className="space-y-2">
                {commonVariables.map((variable) => (
                  <div
                    key={variable.name}
                    className="flex items-center justify-between px-3 py-2.5 bg-surface rounded-lg border border-gray-200 hover:border-gray-300 transition-colors"
                  >
                    <div className="flex items-center gap-2.5">
                      <code className="text-xs font-mono bg-gray-50 px-2 py-1 rounded border border-gray-100 text-gray-800">
                        {`{{${variable.name}}}`}
                      </code>
                      <span
                        className={`text-[10px] px-1.5 py-0.5 rounded border font-medium ${typeColor[variable.type] || typeColor.object}`}
                      >
                        {variable.type}
                      </span>
                      <span className="text-xs text-gray-500">{variable.desc}</span>
                    </div>
                    <CopyButton text={`{{${variable.name}}}`} label={`var-${variable.name}`} />
                  </div>
                ))}
              </div>

              {/* Data iteration example */}
              <div className="mt-6 bg-gray-50 rounded-lg border border-gray-200 p-4">
                <div className="text-[11px] font-medium text-gray-500 uppercase tracking-wider mb-2">
                  Data Iteration Example
                </div>
                <div className="flex items-start justify-between gap-2">
                  <pre className="text-xs font-mono text-gray-700 bg-surface rounded border border-gray-200 p-3 flex-1 overflow-auto">
                    {`{{#each data}}
  {{@index}}. {{this.[Field Name]}} - {{this.[Value]}}
{{/each}}

Total rows: {{meta.rowCount}}
Generated: {{formatDate meta.generatedAt "full"}}`}
                  </pre>
                  <CopyButton
                    text={`{{#each data}}\n  {{@index}}. {{this.[Field Name]}} - {{this.[Value]}}\n{{/each}}\n\nTotal rows: {{meta.rowCount}}\nGenerated: {{formatDate meta.generatedAt "full"}}`}
                    label="data-example"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Helpers Section */}
          {activeSection === 'helpers' && (
            <div className="p-5 space-y-5">
              {helperReference.map((category) => (
                <div key={category.category}>
                  <div className="text-[11px] font-medium text-gray-500 uppercase tracking-wider mb-2">
                    {category.category}
                  </div>
                  <div className="space-y-1.5">
                    {category.items.map((helper) => (
                      <div
                        key={helper.name}
                        className="flex items-center justify-between px-3 py-2.5 bg-surface rounded-lg border border-gray-200 hover:border-gray-300 transition-colors"
                      >
                        <div className="flex items-center gap-2.5 min-w-0">
                          <span className="text-xs font-semibold text-gray-700 w-20 flex-shrink-0">
                            {helper.name}
                          </span>
                          <code className="text-[11px] font-mono text-violet-600 truncate">
                            {helper.syntax}
                          </code>
                          <span className="text-[11px] text-gray-400 hidden lg:inline flex-shrink-0">
                            {helper.desc}
                          </span>
                        </div>
                        <CopyButton text={helper.syntax} label={`helper-${helper.name}`} />
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end px-6 py-4 border-t border-gray-100 bg-gray-50/50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-surface border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default TemplateVariablesModal;
